import { useEffect, useRef } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useI18n, type Lang } from "@/lib/i18n";
import { translateBatch } from "@/lib/translate.functions";

const CACHE_KEY = "auto-translate-cache-v1";
const ORIG_ATTR = "data-orig-text";
const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "INPUT", "SVG", "PATH",
]);
const MIN_LEN = 2;

type Cache = Record<string, Record<string, string>>; // lang -> es -> translated

function loadCache(): Cache {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(CACHE_KEY) || "{}") as Cache;
  } catch {
    return {};
  }
}
function saveCache(c: Cache) {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(c));
  } catch { /* quota */ }
}

function shouldSkip(node: Node): boolean {
  let el: Node | null = node.parentNode;
  while (el && el.nodeType === 1) {
    const tag = (el as Element).tagName;
    if (SKIP_TAGS.has(tag)) return true;
    if ((el as Element).hasAttribute?.("data-no-translate")) return true;
    el = el.parentNode;
  }
  return false;
}

function collectTextNodes(root: Node): Text[] {
  const out: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      const t = n.nodeValue?.trim() ?? "";
      if (t.length < MIN_LEN) return NodeFilter.FILTER_REJECT;
      if (!/\p{L}/u.test(t)) return NodeFilter.FILTER_REJECT;
      if (shouldSkip(n)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n: Node | null;
  while ((n = walker.nextNode())) out.push(n as Text);
  return out;
}

function collectAttrs(root: ParentNode): { el: Element; attr: string }[] {
  const out: { el: Element; attr: string }[] = [];
  const els = root.querySelectorAll<HTMLElement>("[placeholder], [title], [aria-label], [alt]");
  els.forEach((el) => {
    for (const a of ["placeholder", "title", "aria-label", "alt"]) {
      const v = el.getAttribute(a);
      if (v && v.trim().length >= MIN_LEN && /\p{L}/u.test(v)) out.push({ el, attr: a });
    }
  });
  return out;
}

function getOrig(el: Element, key: string): string {
  const stored = el.getAttribute(`${ORIG_ATTR}-${key}`);
  return stored ?? "";
}
function setOrig(el: Element, key: string, val: string) {
  if (!el.hasAttribute(`${ORIG_ATTR}-${key}`)) el.setAttribute(`${ORIG_ATTR}-${key}`, val);
}

export function AutoTranslate() {
  const { lang } = useI18n();
  const translate = useServerFn(translateBatch);
  const runningRef = useRef(false);
  const pendingRef = useRef(false);
  const langRef = useRef<Lang>(lang);

  useEffect(() => {
    langRef.current = lang;
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const restore = () => {
      // Restore text nodes
      document.querySelectorAll<HTMLElement>("[data-tx-node='1']").forEach((el) => {
        const orig = el.getAttribute(ORIG_ATTR);
        if (orig != null) {
          el.textContent = orig;
        }
      });
      // Restore attrs
      document.querySelectorAll<HTMLElement>("*").forEach((el) => {
        for (const a of ["placeholder", "title", "aria-label", "alt"]) {
          const orig = el.getAttribute(`${ORIG_ATTR}-${a}`);
          if (orig != null) el.setAttribute(a, orig);
        }
      });
    };

    const run = async () => {
      const currentLang = langRef.current;
      if (currentLang === "es") {
        restore();
        return;
      }
      if (runningRef.current) {
        pendingRef.current = true;
        return;
      }
      runningRef.current = true;
      try {
        const cache = loadCache();
        const langCache = (cache[currentLang] ??= {});

        // Text nodes
        const textNodes = collectTextNodes(document.body);
        // Wrap each text node in a span holder for stable restoration
        const wrappedNodes: { holder: HTMLElement; orig: string }[] = [];
        for (const tn of textNodes) {
          const parent = tn.parentElement;
          if (!parent) continue;
          const orig = tn.nodeValue ?? "";
          // If parent already tracks this exact node from prior run, reuse
          if (parent.getAttribute("data-tx-node") === "1" && parent.childNodes.length === 1) {
            const savedOrig = parent.getAttribute(ORIG_ATTR) ?? orig;
            wrappedNodes.push({ holder: parent, orig: savedOrig });
            continue;
          }
          // Only wrap when this text node is the sole child, otherwise wrap in a span
          if (parent.childNodes.length === 1 && parent.nodeType === 1) {
            parent.setAttribute("data-tx-node", "1");
            if (!parent.hasAttribute(ORIG_ATTR)) parent.setAttribute(ORIG_ATTR, orig);
            wrappedNodes.push({ holder: parent, orig });
          } else {
            const span = document.createElement("span");
            span.setAttribute("data-tx-node", "1");
            span.setAttribute(ORIG_ATTR, orig);
            span.textContent = orig;
            parent.replaceChild(span, tn);
            wrappedNodes.push({ holder: span, orig });
          }
        }

        // Attrs
        const attrs = collectAttrs(document.body);
        for (const { el, attr } of attrs) {
          const cur = el.getAttribute(attr) ?? "";
          setOrig(el, attr, cur);
        }

        // Apply cached values immediately, collect missing
        const missing = new Set<string>();

        for (const { holder, orig } of wrappedNodes) {
          const key = orig.trim();
          const cached = langCache[key];
          if (cached) {
            holder.textContent = orig.replace(key, cached);
          } else {
            missing.add(key);
          }
        }
        for (const { el, attr } of attrs) {
          const orig = getOrig(el, attr);
          const key = orig.trim();
          if (!key) continue;
          const cached = langCache[key];
          if (cached) el.setAttribute(attr, orig.replace(key, cached));
          else missing.add(key);
        }

        // Batch translate missing (chunks of 60)
        const missingArr = Array.from(missing);
        const CHUNK = 60;
        for (let i = 0; i < missingArr.length; i += CHUNK) {
          const slice = missingArr.slice(i, i + CHUNK);
          try {
            const { translations } = await translate({
              data: { texts: slice, target: currentLang as "en" | "pt" },
            });
            slice.forEach((src, idx) => {
              const tgt = translations[idx];
              if (tgt) langCache[src] = tgt;
            });
            // Apply results
            for (const { holder, orig } of wrappedNodes) {
              const key = orig.trim();
              if (slice.includes(key) && langCache[key]) {
                holder.textContent = orig.replace(key, langCache[key]);
              }
            }
            for (const { el, attr } of attrs) {
              const orig = getOrig(el, attr);
              const key = orig.trim();
              if (slice.includes(key) && langCache[key]) {
                el.setAttribute(attr, orig.replace(key, langCache[key]));
              }
            }
            saveCache(cache);
          } catch (err) {
            console.warn("[auto-translate] batch failed", err);
          }
        }
      } finally {
        runningRef.current = false;
        if (pendingRef.current) {
          pendingRef.current = false;
          setTimeout(run, 50);
        }
      }
    };

    // Initial run
    run();

    // Observe DOM changes (route transitions, dynamic content)
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (langRef.current === "es") return;
      if (scheduled) return;
      scheduled = true;
      setTimeout(() => {
        scheduled = false;
        run();
      }, 250);
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: false });

    return () => observer.disconnect();
  }, [lang, translate]);

  return null;
}
